import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Grid2 as Grid,
  Container,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { DataGrid } from "@mui/x-data-grid";

import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {
  clearPermitPackages,
  getCustomers,
  getPermitPackages,
} from "../../store/actions/dataActions";
import CustomerInfoCard from "./management/CustomerInfoCard";
import { setActiveTab } from "../../store/actions/uiActions/myPermitsActions";

const CustomersGrid = ({
  selectedRow,
  setSelectedRow,
  dataLoading,
  setDataLoading,
}) => {
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.nocData?.customers);
  const { activeTab } = useSelector((state) => state.myPermitsComponent);

  const [packagePage, setPackagePage] = useState(0);
  const [packagePageSize, setpackagePageSize] = useState(5);
  const [packageRowCount, setPackageRowCount] = useState(0);
  const [packageLoading, setPackageLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      if (activeTab !== 0) {
        dispatch(setActiveTab(0));
      }
    }
  }, [activeTab, dispatch]);

  const handleRowClick = (params, event, details) => {
    if (selectedRow?.id !== params?.row?.id) {
      setPackageLoading(true);
      setSelectedPackage(null);
      setSelectedRow(params?.row); // updates state and UI first

      // fire and forget: let this run in background
      (async () => {
        const result = await dispatch(
          getPermitPackages({
            newPage: 0,
            newPageSize: 5,
            customerData: {
              email: params?.row?.email,
              role: params?.row?.role,
            },
          })
        );
        setPackageRowCount(result?.data || 0);
        setpackagePageSize(5);
        setPackagePage(0);
        setPackageLoading(false);
      })();
    }
  };

  const handlePaginationModelChange = async (model, details) => {
    if (dataLoading) {
      return;
    }
    const newPage = model.page;

    setDataLoading(true);
    await dispatch(clearPermitPackages());
    setSelectedPackage(null);
    setSelectedRow(null);
    const resp = await dispatch(getCustomers(newPage));

    if (resp) {
      setDataLoading(false);
    }
  };

  const customersColumn = [
    {
      field: "registrationDate",
      headerName: "Registration Date",
      flex: 1,
      editable: false,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      flex: 1.5,
      editable: false,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 0.5,
      editable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      editable: false,
    },
    {
      field: "tierName",
      headerName: "Base Plan",
      flex: 1,
      editable: false,
    },
    {
      field: "has_active_coupons",
      headerName: "Coupons",
      headerAlign: "center",
      flex: 0.7,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Active",
      headerAlign: "center",
      flex: 0.5,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </Box>
      ),
    },
  ];

  // const handleChartValues = () => {
  //   if (selectedRow) {
  //     let chartList = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

  //     selectedRow?.analytics?.nocUsage.map((item) => {
  //       if (item.year === year) {
  //         chartList[item.month] = item.noc;
  //       }
  //       return null;
  //     });

  //     return chartList;
  //   }
  // };

  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 1,
          backgroundColor: "#EEF2F6",
        }}
      >
        <Container maxWidth="xl">
          <Grid container sx={{ my: 2 }} spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Card>
                <CardHeader title="Customers" />
                <CardContent>
                  <DataGrid
                    sx={{
                      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        "&:hover": {
                          ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                            {
                              visibility: "hidden",
                              pointerEvents: "none",
                            },
                          cursor: "default",
                        },
                        backgroundColor: "#333333", // Change column header background color
                        color: "white", // Optional: Change text color for contrast
                      },
                      // Additionally target specific sort button
                      "& .MuiDataGrid-columnHeader--sortable": {
                        pointerEvents: "none",
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        padding: 0, // Adjust as needed
                        margin: 0, // Adjust as needed
                      },
                      "& .MuiDataGrid-columnHeadersInner": {
                        width: "100%", // Adjust as needed
                        padding: 0,
                      },
                      border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
                      "& .MuiDataGrid-cell": {
                        borderColor: "#333333", // Change cell border color
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        display: "none", // Optional: Hide the column separators
                      },
                      "& .MuiDataGrid-row": {
                        cursor: "pointer", // Change cursor to pointer for rows
                        // backgroundColor: "#fff4c0 !important",
                      },
                      // Apply light yellow background for specified rows
                      "& .light-yellow-row": {
                        backgroundColor: "#fff4c0",
                      },
                      "& .selected-row-color": {
                        backgroundColor: "#ebf1f4 !important",
                      },
                      // Apply darker yellow only for selected rows that are light-yellow
                      "& .light-yellow-row.Mui-selected": {
                        backgroundColor: "#ffe499 !important", // Slightly darker yellow
                      },
                    }}
                    rows={customers?.rows || []}
                    columns={customersColumn}
                    getRowId={(row) => row?.id}
                    onPaginationModelChange={(model, details) => {
                      handlePaginationModelChange(model, details);
                    }}
                    loading={dataLoading}
                    paginationMode="server"
                    paginationModel={{
                      page: customers?.pagination?.page, // Set the current page value here
                      pageSize: customers?.pagination?.rowsPerPage, // Set the page size value here
                    }}
                    rowCount={customers?.pagination?.totalCount}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 5,
                        },
                      },
                    }}
                    onRowClick={(params, event, details) => {
                      handleRowClick(params, event, details);
                    }}
                    pageSizeOptions={[5]}
                    rowHeight={34}
                  />

                  {selectedRow && (
                    <CustomerInfoCard
                      selectedRow={selectedRow}
                      setSelectedRow={setSelectedRow}
                      packagePage={packagePage}
                      setPackagePage={setPackagePage}
                      packagePageSize={packagePageSize}
                      setpackagePageSize={setpackagePageSize}
                      packageLoading={packageLoading}
                      setPackageLoading={setPackageLoading}
                      packageRowCount={packageRowCount}
                      setPackageRowCount={setPackageRowCount}
                      selectedPackage={selectedPackage}
                      setSelectedPackage={setSelectedPackage}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default CustomersGrid;
